// PascalCase to match the LaunchPoint Portal API wire format (no camelCase resolver is
// registered). Mirrors Models/Vault77Health.cs. Query-string params stay camelCase.

export type Vault77HealthIssueCode =
	| "MISSING_PAY_RATE"
	| "NO_CURRENT_PAY_RATE"
	| "MISSING_SENSITIVE_DATA"
	| "REVIEW_WITHOUT_PAY_RATE"
	| "DANGLING_REVIEW_LINK"
	| "DUPLICATE_REVIEW_LINK"
	| "DUPLICATE_EFFECTIVE_DATE"
	| "EFFECTIVE_DATE_DRIFT"

export interface Vault77HealthIssue {
	Code: Vault77HealthIssueCode | string
	Message: string
	/**
	 * Internal Vault77 review id. Diagnostics/fallback only — display ExternalReviewId instead.
	 * It is the only review identifier present for a broken link (review deleted) or an unlinked review.
	 */
	EmployeeReviewId: number | null
	/**
	 * The FieldResponse (legacy) review id — the review id shown everywhere else in the app, so this
	 * is what we surface to users. Null when the review is missing or was never linked to FieldResponse.
	 */
	ExternalReviewId: number | null
	/** Set for pay-rate-scoped issues (deep-link to the rate). */
	PayRateHistoryId: number | null
	EffectiveDate: string | null
}

export interface Vault77EmployeeHealth {
	/** Maps to the HR employee's UserId. */
	EmployeeId: number
	EmployeeName: string
	Issues: Vault77HealthIssue[]
}

export interface Vault77HealthCounts {
	MissingPayRate: number
	NoCurrentPayRate: number
	MissingSensitiveData: number
	ReviewsWithoutPayRate: number
	DanglingReviewLinks: number
	DuplicateReviewLinks: number
	DuplicateEffectiveDates: number
	EffectiveDateDrift: number
}

export interface Vault77HealthReport {
	EmployeesInScope: number
	EmployeesWithIssues: number
	GeneratedDateUtc: string
	Counts: Vault77HealthCounts
	/** Only employees with at least one finding are returned. */
	Employees: Vault77EmployeeHealth[]
}

// Query-string filters — camelCase to match the API's parameter binding.
export interface Vault77HealthFilters {
	departmentId?: number
}

/** Short, human-friendly labels for each issue code (for chips / summaries). */
export const VAULT77_HEALTH_ISSUE_LABELS: Record<string, string> = {
	MISSING_PAY_RATE: "No pay rate",
	NO_CURRENT_PAY_RATE: "No current rate",
	MISSING_SENSITIVE_DATA: "No PII record",
	REVIEW_WITHOUT_PAY_RATE: "Unrecorded pay change",
	DANGLING_REVIEW_LINK: "Broken review link",
	DUPLICATE_REVIEW_LINK: "Duplicate review link",
	DUPLICATE_EFFECTIVE_DATE: "Duplicate effective date",
	EFFECTIVE_DATE_DRIFT: "Effective-date drift",
}

/** Color per issue code — drives the header gauges and the per-row warning icons. */
export const VAULT77_HEALTH_ISSUE_COLORS: Record<string, string> = {
	MISSING_PAY_RATE: "#dc2626", // red — blocks reviews
	NO_CURRENT_PAY_RATE: "#ea580c", // orange
	MISSING_SENSITIVE_DATA: "#2563eb", // blue
	REVIEW_WITHOUT_PAY_RATE: "#ffc107", // amber
	DANGLING_REVIEW_LINK: "#dc2626", // red — integrity
	DUPLICATE_REVIEW_LINK: "#7c3aed", // purple — integrity
	DUPLICATE_EFFECTIVE_DATE: "#7c3aed", // purple — integrity
	EFFECTIVE_DATE_DRIFT: "#0891b2", // teal
}

export const VAULT77_HEALTH_DEFAULT_COLOR = "#6b7280"

/** Order + accessors used to turn the roll-up Counts object into a list of non-zero chips. */
export const VAULT77_HEALTH_SUMMARY_ORDER: Array<{
	code: Vault77HealthIssueCode
	get: (c: Vault77HealthCounts) => number
}> = [
	{ code: "MISSING_PAY_RATE", get: (c) => c.MissingPayRate },
	{ code: "NO_CURRENT_PAY_RATE", get: (c) => c.NoCurrentPayRate },
	{ code: "MISSING_SENSITIVE_DATA", get: (c) => c.MissingSensitiveData },
	{ code: "REVIEW_WITHOUT_PAY_RATE", get: (c) => c.ReviewsWithoutPayRate },
	{ code: "DANGLING_REVIEW_LINK", get: (c) => c.DanglingReviewLinks },
	{ code: "DUPLICATE_REVIEW_LINK", get: (c) => c.DuplicateReviewLinks },
	{ code: "DUPLICATE_EFFECTIVE_DATE", get: (c) => c.DuplicateEffectiveDates },
	{ code: "EFFECTIVE_DATE_DRIFT", get: (c) => c.EffectiveDateDrift },
]
